import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import axios from 'axios';
import { LayoutDashboard, Table, Calendar as CalendarIcon, ArrowLeft, Grid3X3 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SocialMediaLayout = () => {
  const location = useLocation();
  const { token } = useAuth();
  const { hasPageAccess, isAdminRole } = usePermissions();
  const canSeeDashboard = isAdminRole || hasPageAccess('dashboard');
  const [attentionCount, setAttentionCount] = useState(0);

  const loadAttentionCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/social-media/posts/attention-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttentionCount(res.data.count || 0);
    } catch (error) {
      // Non-critical — badge simply won't update this cycle.
    }
  }, [token]);

  useEffect(() => {
    loadAttentionCount();

    // Refresh on any post create/edit/delete/reschedule/status change.
    window.addEventListener('social-media-posts-changed', loadAttentionCount);

    // Keep it fresh even with no activity (e.g. another user's changes).
    const pollInterval = setInterval(loadAttentionCount, 60000);

    // Re-check right at local midnight so "today" rolls over correctly.
    const now = new Date();
    const msUntilMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 5) - now;
    const midnightTimer = setTimeout(loadAttentionCount, msUntilMidnight);

    return () => {
      window.removeEventListener('social-media-posts-changed', loadAttentionCount);
      clearInterval(pollInterval);
      clearTimeout(midnightTimer);
    };
  }, [loadAttentionCount]);

  const tabs = [
    { path: '/social-media', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/social-media/posts', label: 'Posts', icon: Table, badge: attentionCount },
    { path: '/social-media/calendar', label: 'Calendar', icon: CalendarIcon },
    { path: '/social-media/tracker', label: 'Account & College Tracker', icon: Grid3X3 },
  ];

  return (
    <div className="space-y-6">
      {/* Header with Back Link */}
      <div className="flex items-center justify-between">
        <div>
          {canSeeDashboard && (
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-cyan-600 mb-2 transition-colors"
              data-testid="back-to-main-dashboard-link"
            >
              <ArrowLeft size={16} /> Back to Main Dashboard
            </Link>
          )}
          <h1 className="text-3xl font-bold text-slate-900">Social Media Content Tracker</h1>
          <p className="text-slate-600 mt-1">Manage posting schedules and content calendar</p>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="flex gap-1 p-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = location.pathname === tab.path;
            const showBadge = !!tab.badge && tab.badge > 0;
            const badgeLabel = tab.badge > 99 ? '99+' : String(tab.badge);
            
            return (
              <Link
                key={tab.path}
                to={tab.path}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                  isActive
                    ? 'btn-cta text-white shadow-md'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Icon size={18} />
                {tab.label}
                {showBadge && (
                  <span
                    data-testid="social-media-posts-attention-badge"
                    className={`min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full text-[11px] font-bold leading-none ${
                      isActive ? 'bg-brand-lime text-brand-navy' : 'bg-red-500 text-white'
                    }`}
                  >
                    {badgeLabel}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <Outlet />
    </div>
  );
};

export default SocialMediaLayout;
